import { useEffect, useMemo, useState } from "react";
import { ApiError, fetchModelDetail, setModelEnabled, type LedgerRowDto, type ModelDetailDto } from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { exportRowsAsXlsx } from "../exportXlsx";
import { sortGlyph, useSort } from "../hooks/useSort";

const SMALL_N_THRESHOLD = 30;

function fmtMetric(name: string | null, value: number | null): string {
  if (name === null || value === null) return "—";
  if (name === "vol_error_qlike") return value.toFixed(3);
  return `${value >= 0 ? "+" : ""}${value.toFixed(4)}`;
}

function metricLabel(name: string | null): string {
  if (name === "IC") return "rank-IC (predicted vs realized return)";
  if (name === "IC_sigma_rank") return "rank-IC (predicted vs realized sigma)";
  if (name === "vol_error_qlike") return "QLIKE (predicted vs realized variance)";
  return name ?? "metric";
}

function LineChart({ points, height = 150, color = "var(--accent)" }: { points: { x: number; y: number }[]; height?: number; color?: string }) {
  if (points.length < 2) return <div className="note">not enough points to chart</div>;
  const w = 640;
  const padL = 44;
  const padR = 12;
  const padT = 12;
  const padB = 22;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanY = maxY - minY || 1;
  const X = (x: number) => padL + ((x - minX) / (maxX - minX || 1)) * (w - padL - padR);
  const Y = (y: number) => height - padB - ((y - minY) / spanY) * (height - padT - padB);
  const zeroInRange = minY <= 0 && maxY >= 0;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none">
      {zeroInRange && (
        <>
          <line x1={padL} y1={Y(0)} x2={w - padR} y2={Y(0)} stroke="var(--ink-faint)" strokeDasharray="3 3" opacity={0.5} />
          <text x={w - padR} y={Y(0) - 3} textAnchor="end" fontFamily="var(--font-mono)" fontSize={9} fill="var(--ink-faint)">0</text>
        </>
      )}
      <polyline points={points.map((p) => `${X(p.x).toFixed(1)},${Y(p.y).toFixed(1)}`).join(" ")} fill="none" stroke={color} strokeWidth={2} />
      <text x={4} y={Y(maxY) + 4} fontFamily="var(--font-mono)" fontSize={9} fill="var(--ink-faint)">{maxY.toFixed(3)}</text>
      <text x={4} y={Y(minY) + 4} fontFamily="var(--font-mono)" fontSize={9} fill="var(--ink-faint)">{minY.toFixed(3)}</text>
    </svg>
  );
}

// training_run's `horizon_optimizer_result` JSON column is where the backfill
// script (backend/backfill_training_run.py) put winner/bakeoff/walk_forward/
// walk_forward_summary/source_artifact/caveat -- one place to unwrap it.
function optResult(detail: ModelDetailDto): Record<string, unknown> {
  return (detail.training_run?.horizon_optimizer_result as Record<string, unknown> | undefined) ?? {};
}

function ProvRow({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
      <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--ink-soft)", fontSize: "0.76rem" }}>{k}</span>
      <span className="mono" style={{ fontSize: "0.8rem" }}>{v}</span>
    </div>
  );
}

function ScatterChart({ points, height = 220 }: { points: { x: number; y: number }[]; height?: number }) {
  if (points.length === 0) return <div className="note">no resolved predictions yet to scatter</div>;
  const w = 300;
  const pad = 24;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const lo = Math.min(...xs, ...ys);
  const hi = Math.max(...xs, ...ys);
  const X = (v: number) => pad + ((v - lo) / (hi - lo || 1)) * (w - 2 * pad);
  const Y = (v: number) => height - pad - ((v - lo) / (hi - lo || 1)) * (height - 2 * pad);
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet">
      <line x1={X(lo)} y1={Y(lo)} x2={X(hi)} y2={Y(hi)} stroke="var(--ink-faint)" strokeDasharray="3 3" />
      {points.slice(0, 500).map((p, i) => (
        <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={2} fill="var(--accent)" opacity={0.5} />
      ))}
    </svg>
  );
}

export function ModelDetailPage({
  token,
  modelId,
  version,
  onUnauthorized,
  onBack,
  isAdmin,
  onOpenFeature,
}: {
  onOpenFeature?: (featureKey: string) => void;
  token: string;
  modelId: string;
  version: string;
  onUnauthorized: () => void;
  onBack: () => void;
  isAdmin?: boolean;
}) {
  const [detail, setDetail] = useState<ModelDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resFilter, setResFilter] = useState<"" | "resolved" | "pending">("");
  const [toggling, setToggling] = useState(false);
  const [toggleNote, setToggleNote] = useState<string | null>(null);

  async function toggleEnabled() {
    if (!detail) return;
    setToggling(true);
    setToggleNote(null);
    try {
      const res = await setModelEnabled(token, modelId, !detail.enabled, version);
      setDetail({ ...detail, enabled: res.enabled, is_champion: res.is_champion });
      if (res.demoted_champion) {
        setToggleNote("Disabled — this was the slot's champion, so it was also demoted. The slot now has no champion until re-enabled or a real challenger is promoted.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return; }
      setError(err instanceof ApiError ? err.message : "toggle failed");
    } finally {
      setToggling(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    fetchModelDetail(token, modelId, version, 2000)
      .then(setDetail)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof ApiError ? err.message : "request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modelId, version]);

  const ledgerFiltered = useMemo(() => {
    if (!detail) return [];
    if (resFilter === "resolved") return detail.ledger.filter((r) => r.resolved);
    if (resFilter === "pending") return detail.ledger.filter((r) => !r.resolved);
    return detail.ledger;
  }, [detail, resFilter]);

  const scatterPoints = useMemo(() => {
    if (!detail) return [];
    return detail.ledger
      .filter((r) => r.resolved && r.predicted_value !== null && r.realized_value !== null)
      .map((r) => ({ x: r.predicted_value as number, y: r.realized_value as number }));
  }, [detail]);

  const seriesPoints = useMemo(() => {
    if (!detail) return [];
    return detail.metric_series.map((p, i) => ({ x: i, y: p.metric_value }));
  }, [detail]);

  const { sorted: sortedLedger, sortKey: ledgerSortKey, sortDir: ledgerSortDir, toggleSort: toggleLedgerSort } = useSort<LedgerRowDto>(
    ledgerFiltered,
    (row, key) => (row as unknown as Record<string, unknown>)[key],
    "run_date",
  );

  function exportCsv() {
    if (!detail) return;
    exportRowsAsCsv(
      detail.ledger as unknown as Record<string, unknown>[],
      [
        { key: "run_date", label: "Date made" },
        { key: "display_ticker", label: "Ticker" },
        { key: "security_id", label: "ISIN" },
        { key: "predicted_value", label: "Predicted" },
        { key: "realized_value", label: "Realized" },
        { key: "error", label: "Error" },
        { key: "resolved", label: "Resolved" },
      ],
      `${modelId}_ledger.csv`,
    );
  }

  function exportXlsx() {
    if (!detail) return;
    exportRowsAsXlsx(
      detail.ledger as unknown as Record<string, unknown>[],
      [
        { key: "run_date", label: "Date made" },
        { key: "display_ticker", label: "Ticker" },
        { key: "security_id", label: "ISIN" },
        { key: "predicted_value", label: "Predicted" },
        { key: "realized_value", label: "Realized" },
        { key: "error", label: "Error" },
        { key: "resolved", label: "Resolved" },
      ],
      `${modelId}_ledger.xlsx`,
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!detail) return <div className="page-sub">{loading ? "Loading…" : ""}</div>;

  const w60 = detail.windows.find((w) => w.window_days === 60) ?? detail.windows[0];
  const isSmallN = w60 && w60.sample_n < SMALL_N_THRESHOLD;

  return (
    <>
      <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}>
        ← Evaluation
      </button>

      <PageHelper>
        One model's full record: what it was built to do, how it scored in its own pre-launch backtest, and how it has
        actually performed on live, real predictions since. <b>Claim vs reality</b> below is the honest comparison —
        read it side by side, not as a single verdict.
      </PageHelper>

      <div className="card">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.7rem", letterSpacing: "-0.03em" }}>
              {detail.model_id}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <span className={`chip ${detail.is_champion ? "st-champion" : "st-challenger"}`}>
                {detail.is_champion ? "● champion" : "challenger"}
              </span>
              <span className="chip">{detail.slot_key ?? `${detail.level}.${detail.target_ref}`}</span>
              <span className="chip">{detail.model_kind === "convention" ? "convention (monitored, not raced)" : "fitted"}</span> <FieldHelp term={detail.model_kind === "convention" ? "monitored_not_raced" : "model_kind"} />
              <span className="chip">{detail.output_type}{detail.horizon ? ` · h=${detail.horizon}` : ""}</span> <FieldHelp term="output_type" /> <FieldHelp term="horizon" />
              <span className={`chip ${detail.enabled ? "st-champion" : "st-challenger"}`}>{detail.enabled ? "enabled" : "disabled"}</span>
              {isAdmin && (
                <button className="btn ghost sm" onClick={toggleEnabled} disabled={toggling}>
                  {toggling ? "…" : detail.enabled ? "Disable" : "Enable"}
                </button>
              )}
            </div>
            {toggleNote && (
              <div className="banner show" style={{ display: "flex", marginTop: 10, maxWidth: 480 }}>
                <span>⚠</span><span>{toggleNote}</span>
              </div>
            )}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 18, alignItems: "center", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 10, padding: "12px 18px" }}>
            {detail.windows.map((w) => (
              <div key={w.window_days} style={{ textAlign: "center" }}>
                <div className="note">{w.window_days}d {metricLabel(w.metric_name)} <FieldHelp term={w.metric_name === "vol_error_qlike" ? "qlike" : "ic"} /></div>
                <div className="mono" style={{ fontWeight: 700, fontSize: "1.3rem" }}>{fmtMetric(w.metric_name, w.metric_value)}</div>
                <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
                  n=<FieldHelp term="sample_n" />{w.sample_n.toLocaleString()} · {w.pending_n.toLocaleString()} <FieldHelp term="pending_n" />pending
                  {w.sample_n < SMALL_N_THRESHOLD && <span className="gaps-badge" style={{ marginLeft: 6 }} title="Too few resolved predictions to treat this as a verdict">small-n <FieldHelp term="small_n" /></span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        {isSmallN && (
          <div className="banner show" style={{ display: "flex", marginTop: 12 }}>
            <span>⚠</span>
            <span>
              Only {w60.sample_n} resolved predictions at the 60d window. This is <b>too few to say</b> whether the
              live metric reflects real skill or noise — read it as preliminary, not as a confirmed result, however
              it compares to the backtest claim.
            </span>
          </div>
        )}
      </div>

      <div className="card">
        <div className="h3"><span>Features this model uses <span className="note">read from the saved model file · {detail.features.length} features</span></span></div>
        {detail.features_note && <div className="note">{detail.features_note}</div>}
        {detail.features.length > 0 && (
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="r">Rank</th>
                <th className="l">Feature</th>
                <th>Kind</th>
                <th className="r">Importance</th>
              </tr>
            </thead>
            <tbody>
              {[...detail.features]
                .sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9) || a.key.localeCompare(b.key))
                .map((f) => (
                  <tr key={f.key} className={onOpenFeature ? "clickable" : undefined} style={{ cursor: onOpenFeature ? "pointer" : "default" }}
                    onClick={() => onOpenFeature?.(f.key)} title={onOpenFeature ? "Open this feature in Feature Lab" : undefined}>
                    <td className="r mono">{f.rank ?? "—"}</td>
                    <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{f.label ?? f.key}</td>
                    <td><span className="chip">{f.kind ?? "—"}</span></td>
                    <td className="r mono">
                      {f.importance !== null ? f.importance.toExponential(2) : "not measured"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
        <div className="foot-note">
          Rank is permutation importance: after the build, each feature is shuffled across the cross-section on recent live dates and the
          mean drop in rank IC is recorded. A large drop means the model leans on that feature. Caveat: atr7_pct and vol_60d are strongly
          collinear, so importance is split between them and their individual ranks are unstable. Click a row to open its evidence in Feature Lab.
        </div>
      </div>
      <div className="card">
        <div className="h3"><span>Build provenance <FieldHelp term="bake_off" /> <FieldHelp term="walk_forward" /> <span className="note">what was promised at build time — backfilled from the Phase 4 JSON artifact</span></span></div>
        {detail.training_run ? (
          <>
            <div className="prov" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
              <ProvRow k="Locked horizon" v={`${detail.training_run.horizon_selected ?? "—"}d (${String(detail.training_run.horizon_family ?? "family n/a")})`} />
              <ProvRow k="Winning family" v={`${(optResult(detail).winner as { family?: string } | undefined)?.family ?? "—"}`} />
              <ProvRow k="Split date" v={String((detail.training_run.training_window as Record<string, unknown> | undefined)?.split_date ?? "—")} />
              <ProvRow k="Features selected" v={`${(optResult(detail).selected_features as unknown[] | undefined)?.length ?? "—"}`} />
              <ProvRow k="Source artifact" v={String(optResult(detail).source_artifact ?? "—")} />
              <ProvRow
                k="Walk-forward"
                v={
                  optResult(detail).walk_forward_summary
                    ? `${(optResult(detail).walk_forward_summary as { n_folds: number }).n_folds} folds`
                    : "none on record"
                }
              />
            </div>
            {Array.isArray(optResult(detail).bakeoff) && (
              <div style={{ marginTop: 10 }}>
                <div className="note" style={{ marginBottom: 4 }}>Bake-off (single-split, at the locked horizon)</div>
                <table className="stocks-table">
                  <thead><tr><th className="l">Family</th><th className="r">IC <FieldHelp term="ic" /></th><th>Winner</th></tr></thead>
                  <tbody>
                    {(optResult(detail).bakeoff as { family: string; ic: number }[]).map((b) => (
                      <tr key={b.family}>
                        <td className="l mono">{b.family}</td>
                        <td className="r mono">{b.ic >= 0 ? "+" : ""}{b.ic.toFixed(4)}</td>
                        <td>{b.family === (optResult(detail).winner as { family?: string } | undefined)?.family ? <span className="chip st-champion">winner</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {typeof optResult(detail).caveat === "string" && (
              <div className="banner show" style={{ display: "flex", marginTop: 10 }}>
                <span>ⓘ</span>
                <span>{optResult(detail).caveat as string}</span>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="headline">
              {detail.model_kind === "convention" ? "No training_run — convention model, nothing fitted" : "No training_run recorded for this model"}
            </div>
            <div>
              {detail.model_kind === "convention"
                ? "This is a persistence-convention row (data_spec S9.1/S10.9) — there is no ML fit and no backtest claim to record; the pkl_path is deliberately NULL."
                : "This model's backtest claim was not found among the backfilled Phase 4 JSON artifacts — an honest gap, not a hidden section."}
            </div>
          </div>
        )}
        <div className="foot-note">Artifact: <span className="mono">{detail.pkl_path ?? "— (convention model, nothing fitted)"}</span></div>
      </div>

      <div className="card">
        <div className="h3">
          <span>Live prediction ledger <span className="note">every prediction vs what actually happened</span></span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
            <button className="btn ghost sm" onClick={exportXlsx}>Excel</button>
          </div>
        </div>
        <div className="filter-row" style={{ marginBottom: 10 }}>
          <span className="note">filter:</span>
          <select value={resFilter} onChange={(e) => setResFilter(e.target.value as "" | "resolved" | "pending")}>
            <option value="">resolved + pending</option>
            <option value="resolved">resolved only</option>
            <option value="pending">in-flight only</option>
          </select>
          <span className="note" style={{ marginLeft: "auto" }}>{ledgerFiltered.length} of {detail.sample_n + detail.pending_n} rows shown (capped fetch)</span>
        </div>
        <div className="table-wrap" style={{ maxHeight: 340, overflow: "auto" }}>
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="clickable" onClick={() => toggleLedgerSort("run_date")}>Date made {sortGlyph("run_date", ledgerSortKey, ledgerSortDir)}</th>
                <th className="l clickable" onClick={() => toggleLedgerSort("security_id")}>Instrument {sortGlyph("security_id", ledgerSortKey, ledgerSortDir)}</th>
                <th className="r clickable" onClick={() => toggleLedgerSort("predicted_value")}>Predicted {sortGlyph("predicted_value", ledgerSortKey, ledgerSortDir)}</th>
                <th className="r clickable" onClick={() => toggleLedgerSort("realized_value")}>Realized {sortGlyph("realized_value", ledgerSortKey, ledgerSortDir)}</th>
                <th className="r clickable" onClick={() => toggleLedgerSort("error")}>Error {sortGlyph("error", ledgerSortKey, ledgerSortDir)}</th>
                <th className="clickable" onClick={() => toggleLedgerSort("resolved")}>Status {sortGlyph("resolved", ledgerSortKey, ledgerSortDir)}</th>
              </tr>
            </thead>
            <tbody>
              {sortedLedger.slice(0, 300).map((r, i) => (
                <tr key={i}>
                  <td className="mono" style={{ fontSize: "0.78rem" }}>{r.run_date}</td>
                  <td className="l">
                    <div style={{ fontWeight: 600 }}>{r.display_ticker ?? r.security_id}</div>
                    {r.display_ticker && <div className="mut mono" style={{ fontSize: "0.7rem" }}>{r.security_id}</div>}
                  </td>
                  <td className="r mono">{r.predicted_value !== null ? r.predicted_value.toFixed(4) : "—"}</td>
                  <td className="r mono">{r.realized_value !== null ? r.realized_value.toFixed(4) : <span className="mut">—</span>}</td>
                  <td className="r mono">{r.error !== null ? r.error.toFixed(4) : <span className="mut">—</span>}</td>
                  <td>{r.resolved ? <span className="chip st-champion">resolved</span> : <span className="chip" title="Horizon hasn't elapsed yet -- never counted correct/incorrect">in-flight</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="foot-note">
          Sample_n = {detail.sample_n.toLocaleString()} resolved / {detail.pending_n.toLocaleString()} pending overall
          (H3: pending is never counted correct/incorrect). Showing up to 300 of the fetched rows here — export for
          the complete predicted-vs-actual set.
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>Live diagnostics <span className="note">computed from the ledger and model_performance above</span></span></div>
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16 }}>
          <div>
            <div className="note" style={{ marginBottom: 6 }}>{metricLabel(w60?.metric_name ?? null)} over time (60d trailing window, decay watch)</div>
            <LineChart points={seriesPoints} />
            <div className="hint" style={{ fontSize: "0.74rem", color: "var(--ink-faint)", fontStyle: "italic", marginTop: 6 }}>
              Rising = the trailing window is picking up more, and more recent, evidence as the backfill period
              progresses — not necessarily "the model improving," since the window itself widens with data.
            </div>
          </div>
          <div>
            <div className="note" style={{ marginBottom: 6 }}>Predicted vs realized (resolved only, n={scatterPoints.length})</div>
            <ScatterChart points={scatterPoints} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>Claim vs reality <FieldHelp term="claim_vs_reality" /> <span className="note">two differently-scaled measurements — not a verdict</span></span></div>
        {(() => {
          const wfs = optResult(detail).walk_forward_summary as
            | { metric: string; mean: number; sd: number; n_folds: number; n_positive: number }
            | null
            | undefined;
          if (!wfs) {
            const caveat = optResult(detail).caveat;
            return (
              <div className="empty-state">
                <div className="headline">
                  {detail.training_run ? "No nested walk-forward on record — cannot compute claim vs reality" : "No backtest claim on record — cannot compute claim vs reality"}
                </div>
                <div>
                  Live reality alone: <b className="mono">{fmtMetric(w60?.metric_name ?? null, w60?.metric_value ?? null)}</b>{" "}
                  (n={w60?.sample_n.toLocaleString() ?? 0}{w60 && w60.sample_n < SMALL_N_THRESHOLD ? " — small-n, preliminary" : ""}).
                  {typeof caveat === "string" ? ` ${caveat}` : " Without a nested walk-forward there is no backtest number to compare it against — that would be fabricating the missing half of the comparison."}
                </div>
              </div>
            );
          }
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                <div style={{ textAlign: "center" }}>
                  <div className="note">Backtest claim ({metricLabel(wfs.metric)}) <FieldHelp term="walk_forward" /></div>
                  <div className="mono" style={{ fontWeight: 700, fontSize: "1.8rem" }}>{fmtMetric(wfs.metric, wfs.mean)}</div>
                  <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
                    {wfs.n_positive}/{wfs.n_folds} folds positive · sd {wfs.sd.toFixed(4)} · walk-forward, full history
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--ink-faint)" }}>vs</div>
                <div style={{ textAlign: "center" }}>
                  <div className="note">Live reality (early signal)</div>
                  <div className="mono" style={{ fontWeight: 700, fontSize: "1.8rem", color: "var(--ok)" }}>{fmtMetric(w60?.metric_name ?? null, w60?.metric_value ?? null)}</div>
                  <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
                    n={w60?.sample_n.toLocaleString() ?? 0}{w60 && w60.sample_n < SMALL_N_THRESHOLD ? " — small-n" : ""} · ~93-day backfill
                  </div>
                </div>
              </div>
              <div className="banner show" style={{ display: "flex", marginTop: 12 }}>
                <span>ⓘ</span>
                <span>
                  The backtest claim is a walk-forward mean over the model's <b>full historical training panel</b>
                  ({wfs.n_folds} independent folds spanning years); live reality is the <b>~93-day forward sample</b>
                  accumulated so far{w60 && w60.sample_n < SMALL_N_THRESHOLD ? ` (n=${w60.sample_n} — too few to say)` : ""}.
                  These are read side by side, not merged into one verdict: a live number that happens to sit close to
                  or far from the claim does <b>not</b> confirm or refute it at this sample size — that judgment needs
                  the same independent-blocks significance bar the promotion rule uses (Evaluation), not a bare
                  eyeball comparison of two numbers computed at wildly different n.
                </span>
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
}
